import { Exception } from "data/error/Errors";
import language from "data/source/language/language";
import { action, observable } from "mobx";
import * as React from "react";
import { Props } from "wasabi-common";
import Strings from "wasabi-common/lib/types/Strings";

export interface Action extends Props<any> {
  name: string;
  text: string;
  icon?: string;
  color?: string;
  onClick?(event?: any): any;
}

export interface ModalConfirmProps {
  title?: string;
  content?: React.ReactChild;
  yes?: () => any;
  no?: () => any;
}

export class ModalStore {
  @observable
  private _isActive = false;
  private _title = "";
  private _type = "info";
  private _content: React.ReactChild = "";
  private _buttons: Action[] = [];

  public get title(): string {
    return this._title;
  }

  public get type(): string {
    return this._type;
  }

  public get isActive(): boolean {
    return this._isActive;
  }

  public get content(): React.ReactChild {
    return this._content;
  }

  public get buttons(): Action[] {
    return this._buttons;
  }

  @action
  public show(title?: string, content?: React.ReactChild, buttons?: Action[], type?: string) {
    this._title = title;
    this._content = content;
    this._buttons = buttons;
    this._type = type;
    this._isActive = true;
  }

  public showInfo(message: string, params?: Props<any>) {
    this.show("Info", Strings.template(message, params), [
      {
        name: "ok",
        text: "OK",
        color: "#ff7c00",
      },
    ]);
  }

  public showInfoWithAction(message: string, onClickOk: () => any, params?: Props<any>) {
    this.show("Info", Strings.template(message, params), [
      {
        name: "ok",
        text: "OK",
        color: "#ff7c00",
        onClick: onClickOk,
      },
    ]);
  }

  public showInfoWithoutTitle(message: string, params?: Props<any>) {
    this.show("", Strings.template(message, params), [
      {
        name: "ok",
        text: "OK",
        color: "#ff7c00",
      },
    ]);
  }

  public showError(message: string, params?: Props<any>, onClickOk?: () => any) {
    this.showErrorFrom("Error", message, params, onClickOk);
  }

  public showErrorWithoutTitle(message: string, params?: Props<any>, onClickOk?: () => any) {
    this.showErrorFrom("", message, params, onClickOk);
  }

  public showErrorFrom(title: string, message: string, params?: Props<any>, onClickOk?: () => any) {
    this.show(
      title,
      Strings.template(message, params),
      [
        {
          name: "ok",
          text: "OK",
          color: "#ff7c00",
          onClick: onClickOk,
        },
      ],
      "error"
    );
  }

  public showErrorFromLang(error: Exception, ...langs: string[]) {
    const keys: string[] = langs || [];
    const status = `${error.status}`;
    if (status) {
      keys.push(status);
    }
    if (error.code) {
      keys.push(error.code);
    }
    const result = language.value.apply(language, keys) || language.value("occurredError") || error.message;
    this.showError(result);
  }

  public showConfirmDialog(props: ModalConfirmProps) {
    return this.show(props.title, props.content, [
      {
        name: "Yes",
        text: "Yes",
        color: "#ff7c00",
        onClick: props.yes,
      },
      {
        name: "Cancel",
        text: "Cancel",
        onClick: props.no,
      },
    ]);
  }

  @action
  public close() {
    this._title = "";
    this._content = "";
    this._buttons = [];
    this._isActive = false;
    this._type = "info";
  }
}

const modalStore = new ModalStore();

export default modalStore;
